/* eslint-disable brace-style */
import { findChildrenInRange } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import type { CommandProps, Editor, JSONContent, NodeWithPos } from '@tiptap/react';

import type { DocumentChange, Spotlight } from './../../ricos-common-plugins/spotlight/extension';
import {
  BLOCKQUOTE,
  BULLET_LIST_TYPE,
  CODE_BLOCK_TYPE,
  generateId,
  GIPHY_TYPE,
  HEADER_BLOCK,
  IMAGE_TYPE,
  NUMBERED_LIST_TYPE,
  RICOS_ANCHOR_TYPE,
  RICOS_FONT_SIZE_TYPE,
  RICOS_GIPHY_TYPE,
  RICOS_IMAGE_TYPE,
  RICOS_INDENT_TYPE,
  RICOS_LINE_SPACING_TYPE,
  RICOS_LINK_TYPE,
  RICOS_MENTION_TYPE,
  RICOS_TEXT_COLOR_TYPE,
  RICOS_TEXT_HIGHLIGHT_TYPE,
  TIPTAP_CAPTION_TYPE,
  TIPTAP_FIGURE_TYPE,
  TIPTAP_GIF_TYPE,
  TIPTAP_IMAGE_TYPE,
  TIPTAP_VIDEO_TYPE,
  UNSTYLED,
} from './../../ricos-content';
import { unreachable } from './../../ricos-content/ts-utils';
import type { DocumentStyle, Node } from './../../ricos-schema/legacy/wix/rich_content/v1';
import { Decoration_Type, Node_Type } from './../../ricos-schema/legacy/wix/rich_content/v1';
import type { RicosDocumentNode } from './../../ricos-schema/next-schema';
import type {
  ColorType,
  FocusPosition,
  InsertTextOptions,
  OpenModalOptions,
  PluginsDataMap,
  PublicCommands,
  RicosEditorAPI,
  TextAlignment,
  TiptapEditor,
} from './../../ricos-types';
import { DOC_STYLE_TYPES, Mark_Type, ToolbarType } from './../../ricos-types';
import type { NamedLink } from './../../_shared/link/types';
import { resolversById } from './../../toolbars-v3';
import { RESOLVERS_IDS } from './../../toolbars-v3/lib/resolvers-ids';

import { TO_TIPTAP_TYPE } from '../consts';
import { findNodeById } from '../helpers';
import { convertInlineStylesToCSS } from '../helpers/convertInlineStylesToCss';
import type { TiptapAdapterServices } from '../initializeTiptapAdapter';

// !@todo: this shit is too big, fix it! next-major. Resolvers are also not REACTIVE >:(

export interface TiptapAdapter {
  getEditorCommands: RicosEditorAPI['getEditorCommands'];
  getToolbarProps: RicosEditorAPI['getToolbarProps'];
  openModal: RicosEditorAPI['openModal'];
  focus: RicosEditorAPI['focus'];
  blur: RicosEditorAPI['blur'];
  tiptapEditor: TiptapEditor;
  getDocumentStyle: () => DocumentStyle;
  setTiptapContent: (content: JSONContent) => void;
}

export class RichContentAdapter implements TiptapAdapter {
  getToolbarProps: TiptapAdapter['getToolbarProps'];

  private readonly services: TiptapAdapterServices;

  constructor(public tiptapEditor: Editor, services: TiptapAdapterServices) {
    this.tiptapEditor = tiptapEditor;
    this.getEditorCommands = this.getEditorCommands.bind(this);
    this.getToolbarProps = type => {
      const buttons =
        type === ToolbarType.INSERT_PLUGIN
          ? services.plugins.getAddButtons()
          : services.plugins.getTextButtons();
      return {
        buttons: buttons.toExternalToolbarButtonsConfigs(
          this.getEditorCommands(),
          tiptapEditor,
          services.context.isMobile,
          services.getEditorQuery()
        ),
      };
    };
    this.services = services;
  }

  openModal = (options: OpenModalOptions) => {
    const openFn = this.services.plugins.getExports().modals?.[options.type];
    if (!openFn) {
      console.error(
        `No such modal type ${options.type}, are you sure you added appropriate plugin?`
      );
      return;
    }

    openFn(this.tiptapEditor, this.services);
  };

  getContainer = () => {
    return this.tiptapEditor?.options?.element;
  };

  getDocumentStyle = () => this.tiptapEditor?.getJSON()?.attrs?.documentStyle || {};

  focus(focusPosition?: FocusPosition) {
    this.tiptapEditor.commands.focus(focusPosition);
  }

  blur() {
    this.tiptapEditor.commands.blur();
  }

  setTiptapContent(content: JSONContent): void {
    this.tiptapEditor.commands.setContent(content);
  }

  getEditorCommands(): PublicCommands {
    const hasInlineStyle: PublicCommands['hasInlineStyle'] = style => {
      const resolvedStyles = {
        bold: this.services.content.resolve(resolversById[RESOLVERS_IDS.IS_TEXT_CONTAINS_BOLD]),
        italic: this.services.content.resolve(resolversById[RESOLVERS_IDS.IS_TEXT_CONTAINS_ITALIC]),
        underline: this.services.content.resolve(
          resolversById[RESOLVERS_IDS.IS_TEXT_CONTAINS_UNDERLINE]
        ),
        spoiler: this.services.content.resolve(
          resolversById[RESOLVERS_IDS.IS_TEXT_CONTAINS_SPOILER]
        ),
      };
      return resolvedStyles[style as $TSFixMeKey<typeof resolvedStyles>];
    };

    return {
      ...this.editorMocks,
      getContent: this.services.schema.getContent,
      getDocumentStyle: this.getDocumentStyle,
      toggleInlineStyle: inlineStyle => {
        const editorCommand = this.tiptapEditor.chain();
        switch (inlineStyle) {
          case 'bold':
            editorCommand.toggleBold().run();
            return;
          case 'underline':
            editorCommand.toggleUnderline().run();
            return;
          case 'italic':
            editorCommand.toggleItalic().run();
            return;
          case 'spoiler':
            editorCommand.toggleSpoiler().run();
            return;
          case 'not_bold':
          case 'not_italic':
          case 'not_underline':
            // Should these be run?
            // editorCommand.toggleBold().run();
            // editorCommand.toggleItalic().run();
            // editorCommand.toggleUnderline().run();
            return;
          default:
            console.error(unreachable(inlineStyle));
            return;
        }
      },
      hasInlineStyle,
      insertBlock: (pluginType: keyof PluginsDataMap, data = {}) => {
        const type = TO_TIPTAP_TYPE[pluginType];
        let id = '';

        if (!type) {
          console.error(`No such plugin type ${pluginType}`);
          return id;
        }

        let { content, ..._attrs } = data;
        id = data.id || generateId();
        let attrs = { id, ...flatComponentState(_attrs) };

        if (type === TIPTAP_FIGURE_TYPE && !content) {
          const normalizedData = normalizeFigureData(pluginType, data, id);
          content = normalizedData.content;
          attrs = normalizedData.attrs;
        }

        this.tiptapEditor
          .chain()
          .focus()
          .command(adjustSelectionForValidInsertion)
          .insertContent([{ type, attrs, content }])
          .run();
        return id;
      },
      insertBlockWithBlankLines: (
        pluginType: keyof PluginsDataMap,
        data = {},
        settings = { updateSelection: true }
      ) => {
        const type = TO_TIPTAP_TYPE[pluginType];
        let id = '';

        if (!type) {
          console.error(`No such plugin type ${pluginType}`);
          return id;
        }

        id = data.id || generateId();

        let { content, ..._attrs } = data;
        let attrs = { id, ...flatComponentState(_attrs) };

        if (type === TIPTAP_FIGURE_TYPE && !content) {
          const normalizedData = normalizeFigureData(pluginType, data, id);
          content = normalizedData.content;
          attrs = normalizedData.attrs;
        }

        const { updateSelection } = settings;
        this.tiptapEditor
          .chain()
          .focus()
          .command(adjustSelectionForValidInsertion)
          .command(({ commands, dispatch, tr }) => {
            if (!dispatch) {
              return true;
            }

            const cmd = commands.insertContentAt(
              settings.pos === undefined
                ? { from: tr.selection.from, to: tr.selection.to }
                : settings.pos,
              [
                ...(settings.skipBlankLineBefore
                  ? []
                  : [{ type: 'PARAGRAPH', attrs: { id: generateId() } }]),
                { type, attrs, content },
                ...(settings.skipBlankLineAfter
                  ? []
                  : [{ type: 'PARAGRAPH', attrs: { id: generateId() } }]),
              ]
            );

            return dispatch(cmd);
          })
          .command(({ commands, dispatch, tr }) => {
            if (!updateSelection || !dispatch) {
              return false;
            }

            const nodeWithPos = findNodeById(tr, id)[0];

            if (!nodeWithPos) {
              return false;
            }

            const selectCommand =
              updateSelection === 'text'
                ? commands.setTextSelection(nodeWithPos.pos)
                : commands.setNodeSelection(nodeWithPos.pos);

            return dispatch(selectCommand);
          })
          .run();
        return id;
      },
      deleteBlock: nodeId => {
        const nodesWithPos = findNodeById(this.tiptapEditor.state.tr, nodeId);
        if (nodesWithPos[0].node.type.name === TIPTAP_IMAGE_TYPE) {
          const figures = this.tiptapEditor.$nodes(TIPTAP_FIGURE_TYPE);
          const selectedFigure = figures?.find(f => {
            return f.firstChild?.node.attrs.id === nodeId;
          });
          if (selectedFigure) {
            return this.tiptapEditor.commands.deleteNode(selectedFigure.attributes.id);
          }

          return this.tiptapEditor.commands.deleteNode(nodeId);
        }

        return this.tiptapEditor.commands.deleteNode(nodeId);
      },
      setBlock: (nodeId, _, data) => {
        return this.tiptapEditor.commands.setNodeAttrsById(nodeId, flatComponentState(data));
      },
      updateBlock: (nodeId, _, data) => {
        return this.tiptapEditor.commands.updateNodeAttrsById(nodeId, flatComponentState(data));
      },
      getColor: (colorType: ColorType) =>
        this.services.content.resolve(
          resolversById[
            RESOLVERS_IDS[
              colorType === 'ricos-text-color'
                ? 'GET_TEXT_COLOR_IN_SELECTION'
                : 'GET_HIGHLIGHT_COLOR_IN_SELECTION'
            ]
          ]
        ),
      getSelection: () => {
        const {
          state: {
            doc,
            selection: { from, to },
          },
          state,
        } = this.tiptapEditor;

        const selectedNodes: ProseMirrorNode[] = [];

        doc.nodesBetween(from, to, node => {
          selectedNodes.push(node);
        });
        const hasSelectionNodes = selectedNodes.length > 0;

        return {
          isFocused: this.tiptapEditor.isFocused,
          isCollapsed: this.tiptapEditor.state.selection.empty,
          startKey: hasSelectionNodes ? selectedNodes[0].attrs.id : '',
          endKey: hasSelectionNodes ? selectedNodes[selectedNodes.length - 1].attrs.id : '',
          textContent: doc.cut(from, to).textContent,
          selectedNodes,
        };
      },

      getSelectionSlice: () => {
        const state = this.tiptapEditor.state;
        if (this.tiptapEditor.state.selection.empty) {
          return undefined;
        }

        const content = state.selection.content();
        if (
          [TIPTAP_IMAGE_TYPE, TIPTAP_GIF_TYPE, TIPTAP_VIDEO_TYPE].includes(
            content.content.firstChild?.type.name as any
          )
        ) {
          // @TODO now only one case of figure selection fixed, review all cases
          const figureContent = NodeSelection.create(state.doc, state.selection.from - 1)
            .content()
            .toJSON();
          return this.services.converters.fromPmNodeChildren(figureContent) as RicosDocumentNode[];
        }

        return this.services.converters.fromPmNodeChildren(content.toJSON()) as RicosDocumentNode[];
      },

      insertDecoration: (type: string, data, settings) => {
        const decorationCommandMap = {
          [RICOS_LINK_TYPE]: (data: $TSFixMe) => ({
            command: 'setLink' as const,
            args: { link: data },
          }),
          [RICOS_ANCHOR_TYPE]: (data: $TSFixMe) => ({
            command: 'setAnchor' as const,
            args: data,
          }),
          [RICOS_TEXT_COLOR_TYPE]: (data: $TSFixMe) => ({
            command: 'setColor' as const,
            args: data.color,
          }),
          [RICOS_TEXT_HIGHLIGHT_TYPE]: (data: $TSFixMe) => ({
            command: 'setHighlight',
            args: data.color,
          }),
          [RICOS_MENTION_TYPE]: (data: $TSFixMe) => ({
            command: 'insertMention' as const,
            args: data.mention,
          }),
          [RICOS_INDENT_TYPE]: () => ({ command: 'indent', args: undefined }),
          [RICOS_FONT_SIZE_TYPE]: (data: $TSFixMe) => ({
            command: 'setFontSize' as const,
            args: parseInt(data.fontSize, 10),
          }),
          [RICOS_LINE_SPACING_TYPE]: (data: $TSFixMe) => ({
            command: 'setLineSpacings' as const,
            args: data.data,
          }),
        } as const;

        const decorationCommand =
          decorationCommandMap[type as $TSFixMeKey<typeof decorationCommandMap>];
        if (decorationCommand) {
          const { command, args } = decorationCommand(data);
          const editorCommand = settings?.shouldFocus
            ? this.tiptapEditor.chain().focus()
            : this.tiptapEditor.chain();
          // @ts-expect-error $TSFixKey
          editorCommand[command as $TSFixMeKey<typeof editorCommand>](args).run();
        } else {
          console.error(`${type} decoration not supported`);
        }
      },

      insertMultipleAnchors: (anchors: NamedLink[]) => {
        const content = anchors.map(anchor => {
          return this.tiptapEditor.schema.nodes.PARAGRAPH.create(
            null,
            this.tiptapEditor.schema.text(anchor.defaultName || '', [
              this.tiptapEditor.schema.marks.ANCHOR.create(anchor),
              this.tiptapEditor.schema.mark(Mark_Type.UNDERLINE),
            ])
          ).toJSON();
        });
        return this.tiptapEditor.commands.insertContent(content);
      },

      getFontSize: () => {
        return this.services.content.resolve(
          resolversById[RESOLVERS_IDS.GET_FONT_SIZE_IN_SELECTION]
        );
      },
      hasLinkInSelection: (options?: { inclusive?: boolean }) => {
        const {
          state: {
            doc,
            selection: { from, to },
          },
        } = this.tiptapEditor;

        // When 'inclusive' is set to true, the selection expands to include the previous node.
        // This resolves an issue where the cursor placed at the end of a link was returning an
        // empty text node instead of including the link in the selection.
        const selectionFrom = options?.inclusive ? from - 1 : from;

        const marks: Record<string, boolean> = {};
        doc.nodesBetween(selectionFrom, to, node => {
          node.marks.forEach(({ type: { name } }) => {
            marks[name] = true;
          });
        });
        return !!(marks[Decoration_Type.LINK] || marks[Decoration_Type.ANCHOR]);
      },
      selectLinkDecoration: (
        decoration: typeof RICOS_LINK_TYPE | typeof RICOS_ANCHOR_TYPE,
        options?: { inclusive?: boolean }
      ) => {
        const decorationTypeMap: { [key: string]: Decoration_Type } = {
          [RICOS_LINK_TYPE]: Decoration_Type.LINK,
          [RICOS_ANCHOR_TYPE]: Decoration_Type.ANCHOR,
        };

        const decorationMarkType: Decoration_Type = decorationTypeMap[decoration];

        if (!decorationMarkType) {
          console.error(`${decoration} decoration not supported`);
        }

        const {
          state: {
            doc,
            selection: { from, to },
          },
        } = this.tiptapEditor;

        const selectionFrom = options?.inclusive ? from - 1 : from;

        const decoratedNodes = findChildrenInRange(doc, { from: selectionFrom, to }, child => {
          return !!child.marks?.find(mark => mark.type.name === decorationMarkType);
        });
        const nodeWithPos: NodeWithPos | undefined = decoratedNodes[0];

        if (nodeWithPos) {
          nodeWithPos && this.tiptapEditor.commands.setNodeSelection(nodeWithPos.pos);
        } else {
          console.error(`${decoration} node with decoration not found`);
        }
      },
      getLinkDataInSelection: (options?: { inclusive?: boolean }) => {
        const {
          state: {
            doc,
            selection: { from, to },
          },
        } = this.tiptapEditor;

        // When 'inclusive' is set to true, the selection expands to include the previous node.
        // This resolves an issue where the cursor placed at the end of a link was returning an
        // empty text node instead of including the link in the selection.
        const selectionFrom = options?.inclusive ? from - 1 : from;

        let link;
        doc.nodesBetween(selectionFrom, to, node => {
          node.marks.forEach(mark => {
            const {
              type: { name },
            } = mark;
            if (name === Decoration_Type.LINK) {
              link = mark.attrs.link;
            } else if (name === Decoration_Type.ANCHOR) {
              link = { anchor: mark.attrs.anchor };
            }
          });
        });
        return link;
      },
      setBlockType: type => {
        const blockTypeCommandMap = {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          [UNSTYLED]: () => this.tiptapEditor.chain().focus().setParagraph().run(),
          [HEADER_BLOCK.ONE]: () =>
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.tiptapEditor.chain().focus().toggleHeading({ level: 1 }).run(),
          [HEADER_BLOCK.TWO]: () =>
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.tiptapEditor.chain().focus().toggleHeading({ level: 2 }).run(),
          [HEADER_BLOCK.THREE]: () =>
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.tiptapEditor.chain().focus().toggleHeading({ level: 3 }).run(),
          [HEADER_BLOCK.FOUR]: () =>
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.tiptapEditor.chain().focus().toggleHeading({ level: 4 }).run(),
          [HEADER_BLOCK.FIVE]: () =>
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.tiptapEditor.chain().focus().toggleHeading({ level: 5 }).run(),
          [HEADER_BLOCK.SIX]: () =>
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.tiptapEditor.chain().focus().toggleHeading({ level: 6 }).run(),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          [BLOCKQUOTE]: () => this.tiptapEditor.commands.toggleBlockquote(),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          [CODE_BLOCK_TYPE]: () => this.tiptapEditor.commands.toggleCodeBlock(),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          [BULLET_LIST_TYPE]: () => this.tiptapEditor.commands.toggleBulletList(),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          [NUMBERED_LIST_TYPE]: () => this.tiptapEditor.commands.toggleOrderedList(),
        };
        const currentSetBlockTypeCommand = blockTypeCommandMap[type];
        if (currentSetBlockTypeCommand) {
          currentSetBlockTypeCommand();
        } else {
          throw new Error(`${type} block type not supported`);
        }
      },
      // What the hell is block type lol. Is like, collapsible list not a block type?
      isBlockTypeSelected: type => {
        const blockTypeActiveCommandMap = {
          [UNSTYLED]: () => this.tiptapEditor.isActive('unstyled'),
          // This is the ONLY correct way to check if a node/mark is active
          // Node_Type shouldn't be used BUT its better than nothing
          [HEADER_BLOCK.ONE]: () => this.tiptapEditor.isActive(Node_Type.HEADING, { level: 1 }),
          [HEADER_BLOCK.TWO]: () => this.tiptapEditor.isActive(Node_Type.HEADING, { level: 2 }),
          [HEADER_BLOCK.THREE]: () => this.tiptapEditor.isActive(Node_Type.HEADING, { level: 3 }),
          [HEADER_BLOCK.FOUR]: () => this.tiptapEditor.isActive(Node_Type.HEADING, { level: 4 }),
          [HEADER_BLOCK.FIVE]: () => this.tiptapEditor.isActive(Node_Type.HEADING, { level: 5 }),
          [HEADER_BLOCK.SIX]: () => this.tiptapEditor.isActive(Node_Type.HEADING, { level: 6 }),
          [CODE_BLOCK_TYPE]: () => this.tiptapEditor.isActive(Node_Type.CODE_BLOCK),
          [BLOCKQUOTE]: () => this.tiptapEditor.isActive(Node_Type.BLOCKQUOTE),
          [NUMBERED_LIST_TYPE]: () => this.tiptapEditor.isActive(Node_Type.ORDERED_LIST),
          [BULLET_LIST_TYPE]: () => this.tiptapEditor.isActive(Node_Type.BULLETED_LIST),
        };
        const currentBlockTypeActiveCommand = blockTypeActiveCommandMap[type];

        if (currentBlockTypeActiveCommand) {
          return currentBlockTypeActiveCommand();
        } else {
          throw new Error(`${type} block type not supported`);
        }
      },
      deleteDecoration: (type: string) => {
        switch (type) {
          case RICOS_LINK_TYPE:
            this.tiptapEditor.commands.unsetLink();
            return;
          case RICOS_ANCHOR_TYPE:
            this.tiptapEditor.commands.unsetAnchor();
            return;
          case RICOS_INDENT_TYPE:
            this.tiptapEditor.commands.outdent();
            return;
          case RICOS_TEXT_COLOR_TYPE:
            this.tiptapEditor.commands.unsetColor();
            return;
          case RICOS_TEXT_HIGHLIGHT_TYPE:
            this.tiptapEditor.commands.unsetHighlight();
            return;
          default:
            console.error(`delete ${type} decoration type not supported`);
        }
      },
      setTextAlignment: (alignment: TextAlignment) => {
        this.tiptapEditor.chain().focus().setTextAlign(alignment).run();
      },
      undo: () => this.tiptapEditor.chain().focus().undo().run(),
      redo: () => this.tiptapEditor.chain().focus().redo().run(),
      insertText: (text: string, options: InsertTextOptions = { preventAutolink: false }) =>
        this.tiptapEditor
          .chain()
          .focus()
          .command(({ tr }) => {
            tr.insertText(text).setMeta('preventAutolink', options.preventAutolink);
            return true;
          })
          .run(),

      getAllBlocksKeys: () => {
        const keys: string[] = [];
        this.tiptapEditor.state.doc.descendants((node: ProseMirrorNode) => {
          keys.push(node.attrs.id);
        });

        return keys;
      },
      getBlockComponentData: id => {
        const nodesWithPos = findNodeById(this.tiptapEditor.state.tr, id);
        if (nodesWithPos[0]) {
          const { node } = nodesWithPos[0];
          return node.attrs;
        } else {
          console.error('Failed to find node and return its data');
        }
      },

      resetDocumentStyleByNodeType: nodeType => {
        this.tiptapEditor
          .chain()
          .focus()
          .unsetAllMarks()
          .resetDocumentStyleByNodeType(nodeType)
          .run();
      },
      updateDocumentStyle: _documentStyles => {
        this.tiptapEditor.commands.updateDocumentStyleBySelectedNode();
      },
      clearSelectedBlocksInlineStyles: () => {
        this.tiptapEditor.commands.unsetAllMarks();
      },

      getCurrentHeading: () => {
        return this.services.content.resolve(resolversById[RESOLVERS_IDS.GET_HEADING_IN_SELECTION]);
      },
      triggerDecoration: type => {
        const triggerDecorationsMap = {
          [RICOS_MENTION_TYPE]: () =>
            this.tiptapEditor
              .chain()
              .focus()
              .command(({ tr }) => {
                tr.insertText(' @');
                return true;
              })
              .run(),
        };
        triggerDecorationsMap[type]?.();
      },
      softNewLine: () => {
        this.tiptapEditor.commands.setHardBreak();
      },
      clearEditorContent: (triggerUpdate = false) => {
        this.tiptapEditor.commands.clearContent(triggerUpdate);
      },
      setSpotlight: (list: Spotlight[]) => {
        this.tiptapEditor.commands.setSpotlightList(list);
      },
      // applies changes and returns reverse changes
      injectNodes: (args: DocumentChange[]): DocumentChange[] => {
        const reverts: { changes?: DocumentChange[] } = {};
        this.tiptapEditor.commands.injectNodes(args, reverts);
        return reverts.changes || [];
      },
    };
  }

  editorMocks = {
    getAnchorableBlocks: () => ({
      anchorableBlocks: [],
      pluginsIncluded: [],
    }),
    getTextAlignment: (): TextAlignment =>
      this.services.content.resolve(resolversById[RESOLVERS_IDS.GET_ALIGNMENT_IN_SELECTION]),
    isBlockTypeSelected: () => false,
    isUndoStackEmpty: () =>
      this.services.content.resolve(resolversById[RESOLVERS_IDS.IS_UNDO_STACK_EMPTY]),
    isRedoStackEmpty: () =>
      this.services.content.resolve(resolversById[RESOLVERS_IDS.IS_REDO_STACK_EMPTY]),
    getSelectedData: () => 'blah',
    getPluginsList: () => [],
    scrollToBlock: (_nodeId: $TSFixMe) => {},
    isBlockInContent: (_nodeId: $TSFixMe) => false,
    toggleBlockOverlay: (_nodeId: $TSFixMe) => false,
    getBlockSpacing: () => {
      const lineHeight = this.services.content.resolve(
        resolversById[RESOLVERS_IDS.GET_LINE_SPACING_IN_SELECTION]
      );
      const paddingTop = this.services.content.resolve(
        resolversById[RESOLVERS_IDS.GET_LINE_SPACING_BEFORE_SELECTION]
      );
      const paddingBottom = this.services.content.resolve(
        resolversById[RESOLVERS_IDS.GET_LINE_SPACING_AFTER_SELECTION]
      );
      return {
        'line-height': lineHeight,
        'padding-top': paddingTop,
        'padding-bottom': paddingBottom,
      };
    },
    saveEditorState: () => {},
    loadEditorState: () => {},
    saveSelectionState: () => {},
    loadSelectionState: () => {},
    setBlockType: () => {},
    _setSelection: () => {},
    getAnchorBlockInlineStyles: () => {
      return {};
    },
    getInlineStylesInSelection: () => {
      const {
        state: {
          doc,
          selection: { from, to },
        },
      } = this.tiptapEditor;

      let node: Node | undefined;
      doc.nodesBetween(from, to, currNode => {
        if (
          [Node_Type.PARAGRAPH as string, Node_Type.HEADING as string].includes(
            currNode?.type?.name
          )
        ) {
          node = this.services.converters.fromPmNode({
            ...currNode.toJSON(),
            type: currNode?.type?.name,
          });
          return false;
        }
      });
      return node ? convertInlineStylesToCSS(node) : {};
    },
    getWiredFontStyles: () => {
      const container = this.getContainer();
      const styles = window.getComputedStyle(container);

      return Object.entries(DOC_STYLE_TYPES).reduce((acc, [key, docType]) => {
        const toPropertyValue = (property: string) => ({
          [property]: styles.getPropertyValue(`--ricos-custom-${key.toLowerCase()}-${property}`),
        });

        return {
          ...acc,
          [docType]: {
            ...toPropertyValue('font-size'),
            ...toPropertyValue('font-family'),
            ...toPropertyValue('color'),
            ...toPropertyValue('background-color'),
          },
        };
      }, {});
    },
    isAtomicBlockInSelection: () => false,
    isTextBlockInSelection: () => true,
    getAnchorBlockType: () => 'paragraph',
    focus: (focusPosition?: FocusPosition) => this.focus(focusPosition),
  };
}

const flatComponentState = (data: $TSFixMe) => {
  const { componentState, ...rest } = data;
  return { ...(rest || {}), ...(componentState || {}) };
};

/*
 * We have changed how image and video node is represented in the Tiptap state.
 * This function ensures backwards compatibility with the old format.
 * This function should be removed once we no longer expose Tiptap to the outside.
 */
const normalizeFigureData = (pluginType: string, data: Record<string, any>, mediaId: string) => {
  const isImage = [RICOS_IMAGE_TYPE, IMAGE_TYPE].includes(pluginType);
  const isGif = [RICOS_GIPHY_TYPE, GIPHY_TYPE].includes(pluginType);
  return {
    content: [
      {
        type: isImage ? TIPTAP_IMAGE_TYPE : isGif ? TIPTAP_GIF_TYPE : TIPTAP_VIDEO_TYPE,
        attrs: {
          id: mediaId,
          ...data,
        },
      },
      {
        type: TIPTAP_CAPTION_TYPE,
      },
    ],
    attrs: {
      id: generateId(),
    },
  };
};

/**
 * Adjusts the selection to ensure valid insertion of block nodes (e.g. FIGURE).
 * Prevents splitting of blocks or incorrect placement when inserting nodes.
 */
const adjustSelectionForValidInsertion = ({ dispatch, tr }: CommandProps) => {
  if (dispatch === undefined) {
    return true;
  }

  const { selection } = tr;
  const { from, to } = selection;

  const resolvedFrom = tr.doc.resolve(from);
  const resolvedTo = tr.doc.resolve(to);

  // select whole figure if an image node is selected
  if (resolvedFrom.nodeAfter?.type.name === TIPTAP_IMAGE_TYPE && from === to - 1) {
    tr.setSelection(NodeSelection.create(tr.doc, from - 1));
    dispatch(tr);
    return true;
  }

  const isFromCaption = resolvedFrom.parent.type.name === TIPTAP_CAPTION_TYPE;
  const isToCaption = resolvedTo.parent.type.name === TIPTAP_CAPTION_TYPE;

  if (!isFromCaption && !isToCaption) {
    return true;
  }

  // select whole figure if selection is within caption
  if (from === to || resolvedFrom.parent.attrs.id === resolvedTo.parent.attrs.id) {
    const nodeAfter = tr.doc.resolve(resolvedFrom.after());
    tr.setSelection(TextSelection.create(tr.doc, nodeAfter.pos, nodeAfter.pos));
    dispatch(tr);
    return true;
  }

  const posFrom = isFromCaption ? tr.doc.resolve(resolvedFrom.after()).pos : from;
  const posTo = isToCaption ? tr.doc.resolve(resolvedTo.before()).pos - 1 : to;
  tr.setSelection(TextSelection.create(tr.doc, posFrom, posTo));
  dispatch(tr);

  return true;
};
